import React, { useState, useRef } from 'react';
import axios from 'axios';

import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import { toast } from 'react-toastify';
import ActivityConfigSubTable from './ActivityConfigSubTable';
import ProgressMediaConfigTable from './ProgressMediaConfigTable';
import ComponentActivitiesConfigTable from './ComponentActivitiesConfigTable';
import { FormSection, SelectInput, TextInput } from '../CustomerAccounts/LoanFormFields';

import {
  apiList,
  verifyLogin,
} from '../../helpers';
import '../../assets/style/constructionPlanWizard.scss';

// Add / Edit / View a construction plan configuration: four steps in a
// full-screen dialog (the steps are wide tables). Each step saves on its own
// and the next one loads what was saved, so the step order, the save calls
// and the checks are unchanged; only the layout is new.

const STEPS = ['Plan details', 'Activities', 'Media instructions', 'Component activities'];
const FLOORS = [{ id: 1, name: 'GROUND' }, { id: 2, name: 'GROUND +1' }, { id: 3, name: 'GROUND +2' }];

export default function OnBoardingDialog({
  open, handleClose, loanType, subscriberId,
  selectedPlanLoanID,
  planID,
  planName,
  planDescription,
  fetchPlans,
  canEdit,
  generateWOComp,
  floors,
  isCompoRequired,
}) {
  const ActivityConfigSubRef = useRef(null);
  const ProgressMediaConfigRef = useRef(null);
  const ComponentActivitiesConfigRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loanTypeId, setLoanTypeID] = useState(selectedPlanLoanID);
  const [noOfFloors, setNoOfFloors] = useState(floors);
  const [selectedPlanID, setSelectedPlanID] = useState(planID);
  const [desc, setDesc] = useState(planDescription);
  const [generateWOComputation, setGenerateWOComputation] = useState(generateWOComp);
  const [isComponentRequired, setComponentRequired] = useState(isCompoRequired);
  const [name, setName] = useState(planName);
  const [canSave, setCanSave] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const saveConstructionPlanModule = () => {
    if (name.length < 1) {
      toast.error('Please Provide Plan Name');
      setErrors({ name: 'Enter a plan name' });
      return;
    }
    if (loanTypeId === -1) {
      toast.error('Please Select Loan Type');
      setErrors({ loanTypeId: 'Select a loan type' });
      return;
    }

    setLoading(true);
    const { session } = verifyLogin();
    const config = { headers: { session } };
    const params = {
      subscriberId,
      loanTypeId,
      noOfFloors,
      planName: name,
      isComponentRequired,
      generateWOComputation,
      description: desc,
    };
    if (selectedPlanID) {
      params.id = selectedPlanID;
    }
    axios.post(`${apiList.UPDATE_CONSTRUCTION_PLAN_MODULE}`, params, config)
      .then((response) => {
        setLoading(false);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        ActivityConfigSubRef.current
          .handleAutoFilterConfigSub(selectedPlanID
            || response.data.data.id, subscriberId, loanTypeId);
        setSelectedPlanID(selectedPlanID || response.data.data.id);
        fetchPlans();
      })
      .catch((error) => {
        setLoading(false);
        toast.error(`${error.response.data.error || error.response.data.message}`);
      });
  };

  const markCompleteConstructionPlanModule = () => {
    const { session } = verifyLogin();
    const config = { headers: { session } };
    setLoading(true);

    if (selectedPlanID) {
      axios.post(`${apiList.MARK_COMPLETED_CONSTRUCTION_PLAN_MODULE}`, { id: selectedPlanID }, config)
        .then(() => {
          setLoading(false);
          handleClose();
          fetchPlans();
        })
        .catch((error) => {
          setLoading(false);
          toast.error(`${error.response.data.error || error.response.data.message}`);
        });
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      switch (activeStep) {
        case 1:
          ActivityConfigSubRef.current.clearData();
          break;
        case 2:
          ProgressMediaConfigRef.current.clearData();
          ActivityConfigSubRef.current.resetCanSave();
          break;
        case 3:
          ComponentActivitiesConfigRef.current.clearData();
          break;
        default:
          break;
      }
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleNext = () => {
    if (activeStep < 4) {
      switch (activeStep) {
        case 0:
          if (canEdit && canSave) {
            saveConstructionPlanModule();
          } else {
            ActivityConfigSubRef.current
              .handleAutoFilterConfigSub(selectedPlanID, subscriberId, loanTypeId);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
          }
          break;
        case 1:
          if (canEdit && ActivityConfigSubRef.current.getCanSave()) {
            setLoading(true);
            ActivityConfigSubRef.current.handleSubmitData();
          } else {
            ProgressMediaConfigRef.current
              .handleAutoFilterConfigSub(selectedPlanID, subscriberId, loanTypeId);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
          }
          break;
        case 2:
          ComponentActivitiesConfigRef.current
            .getComponentActivitiesConfigData(selectedPlanID, subscriberId);
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
          break;
        case 3:
          if (canEdit && isComponentRequired === 'true') {
            ComponentActivitiesConfigRef.current
              .checkDataAvailable();
          } else if (canEdit && isComponentRequired === 'false') {
            markCompleteConstructionPlanModule();
          } else {
            handleClose();
          }
          break;
        default:
          break;
      }
    }
  };

  const nextLabel = () => {
    if (!canEdit) return activeStep === STEPS.length - 1 ? 'Close' : 'Next';
    if (activeStep < 2) return 'Save & continue';
    if (activeStep === 2) return 'Continue';
    return 'Finish';
  };

  // Plan-details edits mark the step as changed (saved on Continue).
  const edit = (fn) => { setCanSave(true); setErrors({}); fn(); };

  let title = 'Construction plan';
  if (canEdit) title = planID ? 'Edit construction plan' : 'New construction plan';

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      PaperProps={{ className: 'cp-dialog' }}
    >
      <header className='cp-top'>
        <div className='cp-title'>
          <h2>{title}</h2>
          {name ? <span>{name}</span> : null}
        </div>
        <div className='cp-steps'>
          {STEPS.map((label, i) => {
            let state = '';
            if (i < activeStep) state = 'done';
            else if (i === activeStep) state = 'active';
            return (
              <React.Fragment key={label}>
                {i > 0 ? <span className={`cp-step-line ${activeStep >= i ? 'done' : ''}`} /> : null}
                <span className={`cp-step ${state}`} aria-current={i === activeStep ? 'step' : undefined}>
                  <span className='cp-step-num'>{i < activeStep ? <CheckRoundedIcon /> : i + 1}</span>
                  <span className='cp-step-label'>{label}</span>
                </span>
              </React.Fragment>
            );
          })}
        </div>
        <IconButton aria-label='Close' size='small' onClick={handleClose}>
          <CloseRoundedIcon />
        </IconButton>
      </header>

      <main className='cp-body'>
        {/* All steps stay mounted: later steps are driven through their refs. */}
        <div className='cp-inner cp-narrow' style={{ display: activeStep === 0 ? 'block' : 'none' }}>
          <FormSection title='Plan details' icon={<DescriptionOutlined />}>
            <TextInput
              id='name'
              label='Plan name'
              required
              span={6}
              disabled={!canEdit}
              error={errors.name}
              value={name}
              onChange={(e) => edit(() => setName(e.target.value))}
            />
            <SelectInput
              id='loanTypeId'
              label='Loan type'
              required
              span={3}
              disabled={!canEdit}
              error={errors.loanTypeId}
              options={loanType.map((element) => ({ value: element.id, label: element.loanType }))}
              value={loanTypeId === -1 ? null : loanTypeId}
              onChange={(event) => edit(() => setLoanTypeID(event.target.value === '#78' ? -1 : event.target.value))}
            />
            <SelectInput
              id='noOfFloors'
              label='Floors'
              span={3}
              disabled={!canEdit}
              options={FLOORS.map((element) => ({ value: element.id, label: element.name }))}
              value={noOfFloors === -1 ? null : noOfFloors}
              onChange={(event) => edit(() => setNoOfFloors(event.target.value === '#78' ? -1 : event.target.value))}
            />
            <TextInput
              id='desc'
              label='Description'
              span={12}
              multiline
              rows={3}
              disabled={!canEdit}
              value={desc}
              onChange={(e) => edit(() => setDesc(e.target.value))}
            />
            <div className='cp-options lf-span-12'>
              <FormControlLabel
                disabled={!canEdit}
                control={(
                  <Checkbox
                    color='primary'
                    size='small'
                    checked={generateWOComputation === 'true'}
                    onChange={(event) => edit(() => {
                      setGenerateWOComputation(event.target.checked ? 'true' : 'false');
                      setComponentRequired('false');
                    })}
                  />
                )}
                label='Basic construction plan'
              />
              <FormControlLabel
                disabled={!canEdit || generateWOComputation === 'true'}
                control={(
                  <Checkbox
                    color='primary'
                    size='small'
                    checked={isComponentRequired === 'true'}
                    onChange={(event) => edit(() => setComponentRequired(event.target.checked ? 'true' : 'false'))}
                  />
                )}
                label='Consider floor components'
              />
            </div>
          </FormSection>
        </div>

        <div className='cp-inner' style={{ display: activeStep === 1 ? 'block' : 'none' }}>
          <ActivityConfigSubTable
            canEdit={canEdit}
            ref={ActivityConfigSubRef}
            subscriberId={subscriberId}
            loanTypeId={loanTypeId}
            onDataSave={(data) => {
              setLoading(false);
              if (data) {
                ProgressMediaConfigRef.current
                  .handleAutoFilterConfigSub(selectedPlanID, subscriberId, loanTypeId, data);
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
              }
            }}
          />
        </div>
        <div className='cp-inner' style={{ display: activeStep === 2 ? 'block' : 'none' }}>
          <ProgressMediaConfigTable
            canEdit={canEdit}
            ref={ProgressMediaConfigRef}
          />
        </div>
        <div className='cp-inner' style={{ display: activeStep === 3 ? 'block' : 'none' }}>
          <ComponentActivitiesConfigTable
            canEdit={canEdit && isComponentRequired === 'true'}
            componentsOff={isComponentRequired !== 'true'}
            subscriberId={subscriberId}
            ref={ComponentActivitiesConfigRef}
            isDataAvail={(avail) => {
              if (avail) {
                markCompleteConstructionPlanModule();
              } else {
                toast.error('Component Configs should not be empty.');
              }
            }}
          />
        </div>
      </main>

      <footer className='cp-footer'>
        {activeStep > 0 ? (
          <Button
            variant='outlined'
            color='primary'
            startIcon={<ChevronLeftRoundedIcon />}
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </Button>
        ) : (
          <Button color='primary' onClick={handleClose}>Cancel</Button>
        )}
        <span className='cp-count'>{`Step ${activeStep + 1} of ${STEPS.length}`}</span>
        <div className='cp-next'>
          <Button
            variant='contained'
            color='primary'
            disableElevation
            endIcon={activeStep < STEPS.length - 1 ? <ChevronRightRoundedIcon /> : null}
            onClick={handleNext}
            disabled={loading}
          >
            {nextLabel()}
          </Button>
          {loading ? <CircularProgress size={22} className='cp-spinner' /> : null}
        </div>
      </footer>
    </Dialog>
  );
}
